/**
 * The pieces every report screen is built from.
 *
 * Small on purpose. Six reports that each invent their own header, their own period control and
 * their own way of saying "as at" is six screens a reader has to learn separately — which is most
 * of what was wrong with the set this replaced.
 */

import { type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormat } from "@/lib/format";
import { Card, CardTitle, PageHeader, Spinner, ErrorState } from "@/components/ui";

/**
 * The top of a report.
 *
 * Every one of them carries a way back to the hub, because a report is a page somebody arrives at
 * from the index and leaves the same way — and `PageHeader`'s `back` is the control the rest of
 * the product already uses for exactly that.
 */
export function ReportHeader({
  title,
  question,
  subtitle,
  actions,
}: {
  title: string;
  /** The question this report answers, in the words somebody would ask it. */
  question: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <PageHeader
      title={title}
      subtitle={subtitle}
      description={question}
      back={{ to: "/app/reports", label: "All reports" }}
      actions={actions}
    />
  );
}

/**
 * How far back, or how far ahead, a report looks — held in the address.
 *
 * In the URL rather than in component state so a reload, a bookmark and a link pasted to a
 * colleague all land on the same report rather than on the default one. The same reasoning as the
 * support ticket thread and the paged lists: anything a reader chose belongs in the address.
 */
export function usePeriod(key: string, fallback: number): [number, (value: number) => void] {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get(key));
  const value = Number.isFinite(raw) && raw > 0 ? raw : fallback;
  return [
    value,
    (next: number) => {
      const updated = new URLSearchParams(params);
      if (next === fallback) updated.delete(key);
      else updated.set(key, String(next));
      setParams(updated, { replace: true });
    },
  ];
}

/** A row of mutually exclusive choices — twelve months against twenty-four, and nothing else. */
export function PeriodChoice({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: number; label: string }>;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-ink-500">{label}</span>
      <div className="inline-flex overflow-hidden rounded-[var(--radius-control)] border border-ink-200" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
            className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
              option.value === value ? "bg-brand-600 text-white" : "bg-white text-ink-600 hover:bg-ink-50"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * A card holding one chart and the sentence that says what it is for.
 *
 * The sentence is a `hint` on the title rather than a paragraph under it, which is the pattern the
 * rest of the product settled on after every screen turned into a page of grey explanation with
 * the data somewhere inside it.
 */
export function ChartCard({
  title,
  hint,
  action,
  children,
  className = "",
}: {
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardTitle hint={hint} action={action}>
        {title}
      </CardTitle>
      {children}
    </Card>
  );
}

/** Loading and failure, said the same way on every report. */
export function ReportState({ isLoading, error, retry }: { isLoading: boolean; error: unknown; retry: () => void }) {
  if (isLoading) return <Spinner />;
  return <ErrorState error={error} retry={retry} />;
}

/**
 * A figure with a word under it, for the strip across the top of a report.
 *
 * Not `StatTile`: a tile is a link into a list and carries an icon disc, which is right on the
 * dashboard and too heavy four-across above a chart. This is the same information at the weight a
 * report wants — and where a figure does have a list behind it, the report links to it from the
 * table rather than from the number.
 */
export function Figure({
  label,
  value,
  sub,
  tone = "ink",
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "ink" | "good" | "warn" | "bad";
}) {
  const colour =
    tone === "good" ? "text-brand-700" : tone === "warn" ? "text-warn-700" : tone === "bad" ? "text-danger-700" : "text-ink-900";
  return (
    <div className="card p-4">
      <span className="eyebrow">{label}</span>
      <div className={`tabular mt-2 text-[22px] font-semibold leading-none tracking-tight ${colour}`}>{value}</div>
      {sub ? <div className="mt-2 text-xs leading-snug text-ink-500">{sub}</div> : null}
    </div>
  );
}

/** Four figures across, collapsing to two and then one. Used at the top of every report. */
export function FigureRow({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>;
}

/**
 * Money at the weight a headline wants it.
 *
 * `formatMoney`'s compact form is right everywhere but at the bottom of its own scale: below a
 * thousand it falls back to the full figure, so nought comes out as the currency followed by two
 * decimal places — and a tile reading "nought point nought nought" beside four abbreviated
 * millions reads as a mistake rather than as a zero.
 *
 * Dropping a trailing pair of zero decimals is the whole of it. Nothing else changes: an
 * abbreviated figure never has them, so only the small end of the scale is touched, and the
 * currency comes from the agency exactly as it does everywhere else — this never writes a symbol
 * of its own.
 */
export function useCompactMoney(): (value: number) => string {
  const fmt = useFormat();
  return (value: number) => fmt.money(value, { compact: true }).replace(/\.00$/, "");
}
